<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import { uploadImage } from "@/lib/uploads";
import { toDateTimeLocal } from "@/lib/date";
import { fetchPoll, fetchTopics, addPoll, updatePoll } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const LEVELS = ["L1", "L2", "L3", "L4"];
const POLL_TYPES = [{ title: "Multiple Choice", value: "mc" }, { title: "Text Box", value: "tb" }];
const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref("");
const allTopics = ref([]);
let original = null;

const form = ref({ pollType: "mc", question: "", level: [], topics: [], startDate: "", endDate: "", isEnabled: false, answers: [{ id: 1, answer: "", img: "", votes: 0 }], multipleVotes: false, maxVotes: 1 });
const isMc = computed(() => form.value.pollType === "mc");
const answerImgUploader = (file, p) => uploadImage("posts", file, p);

function addAnswer() { form.value.answers.push({ id: form.value.answers.length + 1, answer: "", img: "", votes: 0 }); }
function removeAnswer(i) { if (form.value.answers.length > 1) form.value.answers.splice(i, 1); }

onMounted(async () => {
  loading.value = true;
  try {
    allTopics.value = await fetchTopics().catch(() => []);
    if (isEdit.value) {
      const p = await fetchPoll(id.value);
      if (!p) { notFound.value = true; return; }
      original = p;
      form.value = { pollType: p.pollType ?? "mc", question: p.question ?? "", level: Array.isArray(p.level) ? p.level : [], topics: Array.isArray(p.topics) ? p.topics : [], startDate: toDateTimeLocal(p.startDate), endDate: toDateTimeLocal(p.endDate), isEnabled: !!p.isEnabled, answers: Array.isArray(p.answers) && p.answers.length ? p.answers.map((a, i) => ({ id: a.id ?? i + 1, answer: a.answer ?? "", img: a.img ?? "", votes: a.votes ?? 0 })) : [{ id: 1, answer: "", img: "", votes: 0 }], multipleVotes: !!p.multipleVotes, maxVotes: p.maxVotes ?? 1 };
    }
  } catch { error.value = "Couldn't load the poll."; }
  finally { loading.value = false; }
});

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  const f = form.value;
  if (!f.question.trim()) { validationError.value = "Enter a question."; return; }
  if (!f.startDate || !f.endDate) { validationError.value = "Start and end dates are required."; return; }
  if (isMc.value && f.answers.filter(a => a.answer.trim()).length < 2) { validationError.value = "Add at least two answer options."; return; }
  const common = { ...(original || {}), isEnabled: f.isEnabled, startDate: new Date(f.startDate), endDate: new Date(f.endDate), pollType: f.pollType, level: f.level, question: f.question, topics: f.topics };
  const poll = isMc.value ? { ...common, answers: f.answers.filter(a => a.answer.trim()), multipleVotes: f.multipleVotes, maxVotes: f.multipleVotes ? Number(f.maxVotes) : 1 } : common;
  saving.value = true;
  try {
    if (isEdit.value) await updatePoll(id.value, poll); else await addPoll(poll);
    success.value = true; setTimeout(() => router.push({ name: "polls" }), 800);
  } catch { error.value = "Couldn't save the poll."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'polls' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Poll" : "Add Poll" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Poll not found.</VAlert>
    <VCard v-else class="pa-4" max-width="800">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <VRow>
          <VCol cols="12" md="6">
            <VSelect v-model="form.pollType" label="Poll Type" :items="POLL_TYPES" item-title="title" item-value="value" />
          </VCol>
          <VCol cols="12"><VTextField v-model="form.question" label="Question" /></VCol>
          <VCol cols="12">
            <div class="text-body-2 font-weight-semibold mb-1">Level</div>
            <div class="d-flex gap-4 flex-wrap">
              <VCheckbox v-for="lvl in LEVELS" :key="lvl" v-model="form.level" :label="lvl" :value="lvl" hide-details />
            </div>
          </VCol>
          <VCol cols="12">
            <VAutocomplete v-model="form.topics" label="Topics" :items="allTopics.map(t => ({ title: t.topic, value: t.topic }))" item-title="title" item-value="value" multiple chips closable-chips auto-select-first />
          </VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.startDate" label="Start Date" type="datetime-local" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.endDate" label="End Date" type="datetime-local" /></VCol>
          <VCol cols="12"><VSwitch v-model="form.isEnabled" label="Enabled" hide-details /></VCol>
          <VCol v-if="isMc" cols="12">
            <div class="text-body-2 font-weight-semibold mb-2">Answers</div>
            <div v-for="(ans, i) in form.answers" :key="i" class="d-flex gap-2 align-center mb-3">
              <VTextField v-model="ans.answer" :label="`Answer ${i + 1}`" hide-details class="flex-grow-1" />
              <UploadField v-model="ans.img" kind="image" label="Image" :uploader="answerImgUploader" style="width:200px" />
              <VBtn icon="mdi:close" size="small" variant="text" color="error" @click="removeAnswer(i)" />
            </div>
            <VBtn variant="tonal" prepend-icon="mdi:plus" size="small" @click="addAnswer">Add Answer</VBtn>
          </VCol>
          <VCol v-if="isMc" cols="12" class="d-flex gap-4 align-center">
            <VSwitch v-model="form.multipleVotes" label="Allow multiple votes" hide-details />
            <VTextField v-if="form.multipleVotes" v-model.number="form.maxVotes" label="Max votes" type="number" style="max-width:120px" hide-details />
          </VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4 mt-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving">{{ isEdit ? "Update" : "Add Poll" }}</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'polls' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
